import React, { useState, useEffect, useMemo } from 'react';
import { API } from '../utils/helpers';
import useMobileViewport from '../utils/useMobileViewport';

// ── The customer's quote page (Quotes slice 2, 23 Oct) ───────────────────────
//
// Texted or emailed by the shop. Shows the shop, the bike, every line, the
// total and the shop's note, with Approve and Not now. Self-contained like
// PaymentPage: no app theme, always light, mobile-first. The token in the URL
// is the credential.
//
// What the page shows comes from the snapshot frozen when the quote was sent
// (helpers/quotes.js customerSnapshot), never from the live lines.

const BASE = {
  bg: '#f7f6f3', card: '#ffffff', border: '#e2e5ea', borderSoft: '#eef0f3',
  text: '#1a2233', sub: '#5b6472', muted: '#9aa4b2',
  accent: '#e67e22', headerBg: '#0f1724', danger: '#c0392b', ok: '#27ae60',
};
let C = { ...BASE };

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const longDate = (ymd) => {
  if (!ymd) return '';
  const [y, m, d] = String(ymd).split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
};
const when = (iso) => {
  try { return new Date(iso).toLocaleString('en-US', { month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit' }); } catch (_) { return ''; }
};

export default function QuotePage() {
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [asking, setAsking] = useState(null);   // 'approve' | 'decline' while the note box is open
  const [note, setNote] = useState('');
  const [reason, setReason] = useState('');      // why not now: optional, one tap
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const token = useMemo(() => new URLSearchParams(window.location.search).get('token'), []);

  useMobileViewport();
  // Paint the document light while mounted, as PaymentPage does.
  useEffect(() => {
    const html = document.documentElement;
    const prevHtml = html.style.background;
    const prevBody = document.body.style.background;
    html.style.background = BASE.bg;
    document.body.style.background = BASE.bg;
    return () => { html.style.background = prevHtml; document.body.style.background = prevBody; };
  }, []);

  const take = (d) => {
    if (d.accentColor && /^#[0-9a-fA-F]{6}$/.test(d.accentColor)) C = { ...BASE, accent: d.accentColor };
    setData(d);
  };

  useEffect(() => {
    if (!token) { setLoadError(true); return; }
    fetch(`${API}/api/quotes/public/${encodeURIComponent(token)}`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(take)
      .catch(() => setLoadError(true));
  }, [token]);

  const answer = async () => {
    setBusy(true); setError('');
    try {
      const r = await fetch(`${API}/api/quotes/public/${encodeURIComponent(token)}/${asking}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(asking === 'decline' ? { note, reason } : { note }),
      });
      const d = await r.json().catch(() => null);
      if (!d || !d.state) throw new Error();
      take(d);
      setAsking(null); setNote(''); setReason('');
      window.scrollTo(0, 0);
    } catch (_) {
      setError('That did not go through. Please try again, or call the shop.');
    }
    setBusy(false);
  };

  const callShop = data?.shopPhone ? ` at ${data.shopPhone}` : '';

  const shell = (children) => (
    <div style={{ minHeight: '100dvh', boxSizing: 'border-box', background: C.bg, padding: '24px 12px',
      fontFamily: "'Inter', -apple-system, 'Segoe UI', sans-serif" }}>
      <div style={{ maxWidth: 460, margin: '0 auto', background: C.card, border: `1px solid ${C.border}`,
        borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 3px rgba(15,23,36,0.08)' }}>
        <div style={{ background: C.headerBg, padding: '20px 22px', textAlign: 'center' }}>
          {data?.shopLogoUrl
            ? <img src={data.shopLogoUrl} alt={(data.shopName || '').trim()}
                style={{ maxHeight: 52, maxWidth: 240, objectFit: 'contain', display: 'block', margin: '0 auto' }} />
            : <div style={{ fontSize: 16, fontWeight: 600, color: '#fff' }}>{(data?.shopName || 'Your bike shop').trim()}</div>}
          <div style={{ fontSize: 12, color: '#9aa4b2', marginTop: 5 }}>Your {data?.quote?.kind === 'estimate' ? 'estimate' : 'quote'}</div>
        </div>
        <div style={{ height: 3, background: C.accent }} />
        <div style={{ padding: 22 }}>{children}</div>
      </div>
      <div style={{ textAlign: 'center', marginTop: 14, fontSize: 11, color: C.muted }}>Powered by Spinboard</div>
    </div>
  );

  if (loadError || (data && data.state === 'not_found') || (data && !data.quote)) return shell(
    <div style={{ textAlign: 'center', padding: '20px 0' }}>
      <div style={{ fontSize: 15, color: C.text, marginBottom: 8 }}>We couldn't find this quote.</div>
      <div style={{ fontSize: 13, color: C.sub, lineHeight: 1.6 }}>Please call or text the shop{callShop}.</div>
    </div>
  );
  if (!data) return shell(<div style={{ textAlign: 'center', padding: '40px 0', color: C.sub, fontSize: 14 }}>Loading…</div>);

  const q = data.quote;
  const state = data.state;
  // An estimate (26 Oct) is the customer's ticket as written up; the page
  // calls it that, and otherwise works exactly as a quote does.
  const W = q.kind === 'estimate' ? 'estimate' : 'quote';
  const Wc = W === 'estimate' ? 'Estimate' : 'Quote';

  // Extra work (24 Oct): what is already on the ticket comes first, quieter,
  // and the quoted lines are marked NEW. Only the NEW lines are being approved.
  const ex = q.existing && Array.isArray(q.existing.lines) ? q.existing : null;
  const lineRow = (l, i, list, isNew) => (
    <div key={i} style={{ display: 'flex', gap: 10, padding: '9px 0', borderBottom: i < list.length - 1 ? `1px solid ${C.borderSoft}` : 'none', opacity: ex && !isNew ? 0.75 : 1 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, color: C.text, lineHeight: 1.4 }}>
          {isNew && <span style={{ display: 'inline-block', fontSize: 10, fontWeight: 700, letterSpacing: '0.04em', color: '#fff', background: C.accent, borderRadius: 6, padding: '2px 6px', marginRight: 6, verticalAlign: 'middle' }}>NEW</span>}
          {l.description}
        </div>
        {l.note && <div style={{ fontSize: 12, color: C.sub, lineHeight: 1.5, marginTop: 2, whiteSpace: 'pre-wrap' }}>{l.note}</div>}
        <div style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>
          {l.lineType === 'parts' ? 'Parts' : l.lineType === 'misc' ? 'Miscellaneous' : 'Labor'}
          {Number(l.quantity) > 1 ? ` · ${l.quantity} × ${money(l.unitPrice)}` : ''}
          {l.discountName ? ` · ${l.discountName}` : ''}
          {l.warranty ? ' · Warranty' : ''}
        </div>
      </div>
      <div style={{ fontSize: 13, color: C.text, whiteSpace: 'nowrap' }}>{money(l.subtotal)}</div>
    </div>
  );
  const lines = (
    <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, marginBottom: 18, overflow: 'hidden' }}>
      <div style={{ background: C.bg, padding: '10px 14px', borderBottom: `1px solid ${C.border}` }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{Wc} {q.label}</div>
        {q.bike && <div style={{ fontSize: 12, color: C.sub, marginTop: 2 }}>{q.bike}</div>}
      </div>
      {ex && ex.lines.length > 0 && (
        <>
          <div style={{ padding: '10px 14px 0', fontSize: 11, fontWeight: 600, color: C.sub, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Already on your ticket</div>
          <div style={{ padding: '0 14px 4px' }}>{ex.lines.map((l, i) => lineRow(l, i, ex.lines, false))}</div>
          <div style={{ padding: '10px 14px 0', fontSize: 11, fontWeight: 600, color: C.accent, textTransform: 'uppercase', letterSpacing: '0.05em', borderTop: `1px solid ${C.border}` }}>Added work, for your approval</div>
        </>
      )}
      <div style={{ padding: '4px 14px' }}>
        {q.lines.map((l, i) => lineRow(l, i, q.lines, !!ex))}
      </div>
      <div style={{ padding: '10px 14px', borderTop: `1px solid ${C.border}`, background: C.bg }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, color: C.sub, marginBottom: 4 }}>
          <span>{ex ? 'Added work subtotal' : 'Subtotal'}</span><span>{money(q.subtotal)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, color: C.sub, marginBottom: 4 }}>
          {/* The rate is stored as Lightspeed gives it (0.06625); shown as a percent. */}
          <span>Tax{q.taxRate ? ` (${+(Number(q.taxRate) < 1 ? Number(q.taxRate) * 100 : Number(q.taxRate)).toFixed(3)}%)` : ''}</span><span>{money(q.tax)}</span>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, fontWeight: 600, color: C.text }}>
          <span>{ex ? 'Added work total' : 'Total'}</span><span>{money(q.total)}</span>
        </div>
        {ex && (
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, color: C.sub, marginTop: 6, paddingTop: 6, borderTop: `1px solid ${C.borderSoft}` }}>
            <span>Your ticket with this work</span><span>{money(Number(ex.total) + Number(q.total))}</span>
          </div>
        )}
      </div>
    </div>
  );

  // Manny, 24 Oct: say plainly that the quote may not be the last word.
  const partsCaveat = (
    <div style={{ fontSize: 12, color: C.sub, lineHeight: 1.6, marginBottom: 16, padding: '10px 12px', border: `1px solid ${C.borderSoft}`, borderRadius: 8 }}>
      {`Please note: once work begins, additional parts may be needed, and they may not yet be reflected on this ${W}.`}
    </div>
  );

  const shopNote = q.noteToCustomer ? (
    <div style={{ background: C.bg, borderRadius: 10, padding: '12px 14px', marginBottom: 18, fontSize: 13, color: C.text, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
      {q.noteToCustomer}
    </div>
  ) : null;

  const banner = (tone, title, body) => (
    <div style={{ textAlign: 'center', marginBottom: 18 }}>
      <div style={{ width: 48, height: 48, borderRadius: '50%', margin: '0 auto 12px', fontSize: 22, lineHeight: '48px',
        background: tone === 'ok' ? '#eafaf1' : '#fdf2e9', color: tone === 'ok' ? C.ok : C.accent }}>{tone === 'ok' ? '✓' : '–'}</div>
      <div style={{ fontSize: 17, fontWeight: 600, color: C.text }}>{title}</div>
      {body && <div style={{ fontSize: 13, color: C.sub, lineHeight: 1.6, marginTop: 6 }}>{body}</div>}
    </div>
  );

  if (state === 'approved') return shell(
    <>
      {banner('ok', `${Wc} approved`, data.decidedByShop
        ? `Approved with the shop${data.decidedAt ? ` on ${when(data.decidedAt)}` : ''}: ${money(q.total)}. Thanks${q.firstName ? `, ${q.firstName}` : ''}. The shop will be in touch about next steps.`
        : `Thanks${q.firstName ? `, ${q.firstName}` : ''}. You approved ${money(q.total)}${data.decidedAt ? ` on ${when(data.decidedAt)}` : ''}. The shop will be in touch about next steps.`)}
      {lines}
      {data.decisionNote && !data.decidedByShop && <div style={{ fontSize: 12.5, color: C.sub, marginBottom: 12 }}>Your note: “{data.decisionNote}”</div>}
      <div style={{ fontSize: 11.5, color: C.muted, textAlign: 'center' }}>Keep this link to look at your {W} again. Questions? Call the shop{callShop}.</div>
    </>
  );

  // Removed by the shop (24 Oct).
  if (state === 'withdrawn') return shell(
    <div style={{ textAlign: 'center', padding: '20px 0' }}>
      <div style={{ fontSize: 15, color: C.text, marginBottom: 8 }}>This {W} is no longer available.</div>
      <div style={{ fontSize: 13, color: C.sub, lineHeight: 1.6 }}>Questions? Please call or text the shop{callShop}.</div>
    </div>
  );

  // Revisions (24 Oct): the shop sent a newer version; this one can no longer
  // be answered. The page shows what it said, and the way to the new one.
  if (state === 'replaced') return shell(
    <>
      {banner('warn', `This ${W} has been updated`, `${q.firstName ? `${q.firstName}, the` : 'The'} shop has sent you a newer version. Please look at that one: this version can no longer be approved.`)}
      {data.newerUrl && (
        <a href={data.newerUrl}
          style={{ display: 'block', textAlign: 'center', padding: 14, borderRadius: 10, background: C.accent, color: '#fff', fontSize: 15, fontWeight: 600, textDecoration: 'none', marginBottom: 16 }}>
          See the updated {W}
        </a>
      )}
      <div style={{ opacity: 0.55 }}>{lines}</div>
      <div style={{ fontSize: 11.5, color: C.muted, textAlign: 'center' }}>Questions? Call the shop{callShop}.</div>
    </>
  );

  if (state === 'expired') return shell(
    <>
      {banner('warn', `This ${W} has expired`, `It was good until ${longDate(q.expiresOn)}. Prices may have changed: please call or text the shop${callShop} for an updated ${W}.`)}
      {lines}
    </>
  );

  const noteBox = asking && (
    <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, padding: 14, marginBottom: 12 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 8 }}>
        {asking === 'approve' ? `Approve ${money(q.total)}?` : 'Not right now?'}
      </div>
      {asking === 'decline' && (
        <>
          <div style={{ fontSize: 12.5, color: C.sub, marginBottom: 8 }}>Mind telling us why? It helps the shop.</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
            {(data.declineReasons || []).map(r => {
              const on = reason === r.key;
              return (
                <button key={r.key} type="button" onClick={() => setReason(on ? '' : r.key)} aria-pressed={on}
                  style={{ padding: '8px 12px', borderRadius: 18, fontSize: 13, fontFamily: 'inherit', cursor: 'pointer',
                    border: `1px solid ${on ? C.headerBg : C.border}`, background: on ? C.headerBg : '#fff', color: on ? '#fff' : C.text }}>
                  {r.label}
                </button>
              );
            })}
          </div>
        </>
      )}
      <textarea value={note} onChange={e => setNote(e.target.value)} rows={3} maxLength={1000}
        placeholder={asking === 'approve' ? 'Anything the shop should know? (optional)' : reason === 'other' ? 'What is it? (optional)' : 'Anything else, or a question for the shop? (optional)'}
        style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', fontSize: 14, borderRadius: 8,
          border: `1px solid ${C.border}`, fontFamily: 'inherit', resize: 'vertical', color: C.text }} />
      {error && <div style={{ fontSize: 13, color: C.danger, marginTop: 8 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <button onClick={() => { setAsking(null); setError(''); }} disabled={busy}
          style={{ flex: 1, padding: 12, borderRadius: 10, border: `1px solid ${C.border}`, background: '#fff', color: C.sub, fontSize: 14, fontFamily: 'inherit', cursor: 'pointer' }}>Back</button>
        <button onClick={answer} disabled={busy}
          style={{ flex: 2, padding: 12, borderRadius: 10, border: 'none', fontSize: 14, fontWeight: 600, fontFamily: 'inherit', cursor: busy ? 'default' : 'pointer',
            background: asking === 'approve' ? C.accent : C.headerBg, color: '#fff', opacity: busy ? 0.6 : 1 }}>
          {busy ? 'Sending…' : asking === 'approve' ? 'Yes, approve' : 'Send to the shop'}
        </button>
      </div>
    </div>
  );

  return shell(
    <>
      {state === 'declined'
        ? banner('warn', data.decidedByShop ? 'Not now, for the time being' : 'You told the shop not now', `${data.decidedAt ? `On ${when(data.decidedAt)}. ` : ''}Changed your mind? You can still approve it until ${longDate(q.expiresOn)}.`)
        : (
          <>
            <div style={{ fontSize: 15, color: C.text, marginBottom: 4 }}>Hi{q.firstName ? ` ${q.firstName}` : ''},</div>
            <div style={{ fontSize: 13, color: C.sub, lineHeight: 1.6, marginBottom: 14 }}>
              {W === 'estimate' ? 'Here is our estimate' : "Here's what we'd do"}{q.bike ? ` ${W === 'estimate' ? 'for' : 'on'} your ${q.bike}` : ''}. Good until {longDate(q.expiresOn)}.
            </div>
            <div style={{ fontSize: 28, fontWeight: 600, color: C.text, marginBottom: ex ? 2 : 16 }}>{money(q.total)}</div>
            {ex && <div style={{ fontSize: 12.5, color: C.sub, marginBottom: 16 }}>for the added work, marked NEW below</div>}
          </>
        )}
      {shopNote}
      {lines}
      {partsCaveat}
      {noteBox || (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <button onClick={() => setAsking('approve')}
            style={{ padding: 14, borderRadius: 10, border: 'none', background: C.accent, color: '#fff', fontSize: 15, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer' }}>
            Approve {money(q.total)}{ex ? ' added work' : ''}
          </button>
          {state !== 'declined' && (
            <button onClick={() => setAsking('decline')}
              style={{ padding: 13, borderRadius: 10, border: `1px solid ${C.border}`, background: '#fff', color: C.text, fontSize: 14, fontFamily: 'inherit', cursor: 'pointer' }}>
              Not now
            </button>
          )}
        </div>
      )}
      <div style={{ fontSize: 11.5, color: C.muted, textAlign: 'center', marginTop: 14, lineHeight: 1.5 }}>
        Questions? Call or text the shop{callShop}.
      </div>
    </>
  );
}
